import { useEffect } from 'react';

import type { MediaKind, RemoteParticipant, RemoteParticipantEvent, RemoteParticipantEventMap } from '../video-sdk';

function mediaKindToTracksKey(mediaKind: MediaKind) {
  switch (mediaKind) {
    case 'audio':
      return 'audioTracks';
    case 'video':
      return 'videoTracks';
    case 'data':
      return 'dataTracks';
    default:
      return null;
  }
}

export type PrimaryEvents = Pick<RemoteParticipantEventMap, 'mediaSubscribed' | 'mediaUnsubscribed' | 'mediaEnabled'>;

/**
 * Attaches and detaches event listeners to a remote participant for track
 * events. It also handles already published tracks.
 */
export function useRemoteMediaEventListeners({
  mediaKind,
  remoteParticipant,
  remoteMediaEventListeners,
}: {
  mediaKind: MediaKind;
  remoteParticipant?: RemoteParticipant;
  remoteMediaEventListeners?: PrimaryEvents;
}) {
  useEffect(
    function handleRemoteParticipantMediaEvents() {
      const tracksKey = mediaKindToTracksKey(mediaKind);
      if (!remoteParticipant || !remoteMediaEventListeners || !tracksKey) return;

      const detachEventListenerCallbacks = Object.entries(remoteMediaEventListeners).map(([event, callback]) => {
        return remoteParticipant.on(event as RemoteParticipantEvent, callback, {
          mediaKind,
        });
      });

      // Use encapsulated method for audio/video streams
      if (remoteMediaEventListeners.mediaSubscribed) {
        remoteParticipant.dispatchEvent('mediaSubscribed', remoteMediaEventListeners.mediaSubscribed, mediaKind);
      }

      return function removeListeners() {
        if (!remoteParticipant) return;
        for (const callback of detachEventListenerCallbacks) {
          callback();
        }
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [remoteParticipant, remoteMediaEventListeners, mediaKind],
  );
}
